import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import { getArtists, createArtist, updateArtist, deleteArtist, uploadFile, getImageUrl } from '../../utils/api';
import { toast } from 'sonner';

const ArtistManagement = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    genre: '',
    bio: '',
    image: '',
    hero_image: '',
    social_links: {
      instagram: '',
      spotify: '',
      soundcloud: '',
      youtube: '',
      twitter: '',
      tiktok: '',
      bandcamp: '',
      beatport: '',
      apple_music: '',
      facebook: ''
    },
    featured: false
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const data = await getArtists();
      setArtists(data);
    } catch (error) {
      toast.error('Failed to load artists');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, field = 'image') => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadFile(file);
      setFormData(prev => ({ ...prev, [field]: result.url }));
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to upload image. Please try again.');
      }
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArtist) {
        await updateArtist(editingArtist.id, formData);
        toast.success('Artist updated');
      } else {
        await createArtist(formData);
        toast.success('Artist created');
      }
      fetchArtists();
      closeModal();
    } catch (error) {
      toast.error('Failed to save artist');
    }
  };

  const handleEdit = (artist) => {
    setEditingArtist(artist);
    setFormData({
      name: artist.name,
      genre: artist.genre,
      bio: artist.bio,
      image: artist.image || '',
      hero_image: artist.hero_image || '',
      social_links: {
        instagram: artist.social_links?.instagram || '',
        spotify: artist.social_links?.spotify || '',
        soundcloud: artist.social_links?.soundcloud || '',
        youtube: artist.social_links?.youtube || '',
        twitter: artist.social_links?.twitter || '',
        tiktok: artist.social_links?.tiktok || '',
        bandcamp: artist.social_links?.bandcamp || '',
        beatport: artist.social_links?.beatport || '',
        apple_music: artist.social_links?.apple_music || '',
        facebook: artist.social_links?.facebook || ''
      },
      featured: artist.featured || false
    });
    setShowModal(true);
  };

  const handleDelete = async (artistId) => {
    if (!window.confirm('Are you sure you want to delete this artist?')) return;
    
    try {
      await deleteArtist(artistId);
      toast.success('Artist deleted');
      fetchArtists();
    } catch (error) {
      toast.error('Failed to delete artist');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingArtist(null);
    setFormData({
      name: '',
      genre: '',
      bio: '',
      image: '',
      hero_image: '',
      social_links: {
        instagram: '',
        spotify: '',
        soundcloud: '',
        youtube: '',
        twitter: '',
        tiktok: '',
        bandcamp: '',
        beatport: '',
        apple_music: '',
        facebook: ''
      },
      featured: false
    });
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Public Profiles</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add Artist
        </button>
      </div>

      <div className="admin-table-container">
        {artists.length === 0 && (
          <div style={{padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--light-grey)'}}>
            No artists yet. Click "Add Artist" to create one.
          </div>
        )}
        {artists.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Genre</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {artists.map((artist) => {
                return (
                  <tr key={artist.id}>
                    <td>
                      {artist.image && (
                        <img src={getImageUrl(artist.image)} alt={artist.name} style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px'}} />
                      )}
                    </td>
                    <td>{artist.name}</td>
                    <td>{artist.genre}</td>
                    <td>{artist.featured ? 'Yes' : 'No'}</td>
                    <td>
                      <div className="table-actions">
                        <button className="action-btn" onClick={() => handleEdit(artist)}>
                          <Edit size={16} /> Edit
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(artist.id)}>
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingArtist ? 'Edit Artist' : 'Add Artist'}</h2>
              <button className="close-btn" onClick={closeModal} type="button">×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Artist Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Genre *</label>
                  <input
                    type="text"
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Bio *</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label>Profile Image</label>
                <span className="dimension-hint">Recommended: 500 x 500px (square)</span>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'image')}
                    id="artist-image"
                    style={{display: 'none'}}
                  />
                  <label htmlFor="artist-image" className="upload-btn">
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Upload Profile Image'}
                  </label>
                  {formData.image && (
                    <img src={getImageUrl(formData.image)} alt="Preview" className="image-preview" />
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Hero Background Image</label>
                <span className="dimension-hint">Recommended: 1920 x 600px (wide banner)</span>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'hero_image')}
                    id="artist-hero-image"
                    style={{display: 'none'}}
                  />
                  <label htmlFor="artist-hero-image" className="upload-btn">
                    <Upload size={20} />
                    Upload Hero Image
                  </label>
                  {formData.hero_image && (
                    <img src={getImageUrl(formData.hero_image)} alt="Hero Preview" className="image-preview" />
                  )}
                </div>
              </div>

              <div className="form-group">
                <h3>Social Links</h3>
                <div className="social-inputs">
                  <input
                    type="text"
                    placeholder="Instagram URL"
                    value={formData.social_links.instagram}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, instagram: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="Spotify URL"
                    value={formData.social_links.spotify}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, spotify: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="Apple Music URL"
                    value={formData.social_links.apple_music}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, apple_music: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="SoundCloud URL"
                    value={formData.social_links.soundcloud}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, soundcloud: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="YouTube URL"
                    value={formData.social_links.youtube}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, youtube: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="Twitter/X URL"
                    value={formData.social_links.twitter}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, twitter: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="TikTok URL"
                    value={formData.social_links.tiktok}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, tiktok: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="Facebook URL"
                    value={formData.social_links.facebook}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, facebook: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="Bandcamp URL"
                    value={formData.social_links.bandcamp}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, bandcamp: e.target.value }
                    })}
                  />
                  <input
                    type="text"
                    placeholder="Beatport URL"
                    value={formData.social_links.beatport}
                    onChange={(e) => setFormData({
                      ...formData,
                      social_links: { ...formData.social_links, beatport: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  />
                  Featured Artist (show on homepage)
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingArtist ? 'Update Artist' : 'Create Artist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistManagement;
